// src/store/index.tsx
import type { ReactNode } from "react";
import { create } from "zustand";
import { nextSavedWebsites } from "../utils/saved-websites";

/**
 * Why the upgrade modal opened, declared once.
 *
 * It used to be written out three times — on the state, on the action payload, and on the action
 * creator — and only the creator listed `"welcome"`. App.tsx dispatches exactly that on first run,
 * and `UpgradeModal` renders a `"welcome"` case, so the two declarations that omitted it were
 * describing a value the app stores and shows. Nothing caught it because the reducer reads its
 * payload through a helper that returns `any`.
 */
export type UpgradeReason = "quota_exceeded" | "pro_feature" | "general" | "welcome";

// Define state structure
export interface AppState {
  // Website capture state
  url: string;
  urls: string[];
  batchMode: boolean;
  viewports: string[];
  theme: string;

  // Import state
  uploadedFileName: string | null;
  parsedContent: any | null;

  // Shared state
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
  availableFonts: Array<{ value: string; text: string }>;

  // User info
  figmaUID: string;
  figmaUserName: string;
  membership: string;
  isBlocked: boolean;
  blockedUntil: number | null;
  userStatus?: {
    rateLimit: {
      nextAvailableTime: string;
      cooldownRemaining?: number;
    } | null;
  };

  // Storage
  savedWebsites: string[];

  // Options
  options: {
    useAutoLayout: boolean;
    defaultFontFamily: string;
    missingFontBehavior: "replace" | "keep";
    fontMappings?: Array<{ original: string; matched: string }>;
    extractImagesToSections?: boolean;
    extractStyles?: boolean;
    // Import options modal (mirrors the product's Options/Components/Enhancements groups)
    useExistingStyles?: boolean;
    hoverComponents?: boolean;
    addHyperlinks?: boolean;
    htmlLayerNames?: boolean;
    /**
     * Predict Figma auto-layout with @open-pencil/core before importing, and skip the containers
     * it would break. Off by default — it fetches a ~390KB (gzipped) layout engine.
     */
  };

  // Upgrade modal state
  upgradeModal: {
    isOpen: boolean;
    reason?: UpgradeReason;
    rateLimitEndTime?: string | null;
  };

  // Post-import "Import completed" modal state
  importCompleted: {
    isOpen: boolean;
    sectionId: string | null;
    /**
     * The frames the import created, carried alongside `sectionId` because a container is not
     * guaranteed: Buzz has none at all, and in Slides it is a SLIDE rather than a SECTION. Font
     * remapping scopes to these when there is no usable container.
     */
    frameIds: string[];
    fontReplacements: Array<{ original: string; matched: string; downloadUrl?: string }>;
  };
}

// Define action types
export const ActionTypes = {
  // Website capture actions
  SET_URL: "SET_URL",
  SET_URLS: "SET_URLS",
  ADD_URL: "ADD_URL",
  REMOVE_URL: "REMOVE_URL",
  TOGGLE_BATCH_MODE: "TOGGLE_BATCH_MODE",
  SET_VIEWPORTS: "SET_VIEWPORTS",
  SET_THEME: "SET_THEME",

  // Import actions
  SET_UPLOADED_FILE: "SET_UPLOADED_FILE",
  SET_PARSED_CONTENT: "SET_PARSED_CONTENT",
  RESET_IMPORT: "RESET_IMPORT",

  // Shared state actions
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_SUCCESS: "SET_SUCCESS",
  SET_AVAILABLE_FONTS: "SET_AVAILABLE_FONTS",

  // User info actions
  SET_USER_INFO: "SET_USER_INFO",
  SET_MEMBERSHIP: "SET_MEMBERSHIP",
  SET_BLOCKED: "SET_BLOCKED",
  SET_USER_STATUS: "SET_USER_STATUS",

  // Storage actions
  ADD_SAVED_WEBSITE: "ADD_SAVED_WEBSITE",
  SET_SAVED_WEBSITES: "SET_SAVED_WEBSITES",

  // Options actions
  UPDATE_OPTION: "UPDATE_OPTION",

  // Upgrade modal actions
  OPEN_UPGRADE_MODAL: "OPEN_UPGRADE_MODAL",
  CLOSE_UPGRADE_MODAL: "CLOSE_UPGRADE_MODAL",

  // Import completed modal actions
  OPEN_IMPORT_COMPLETED: "OPEN_IMPORT_COMPLETED",
  CLOSE_IMPORT_COMPLETED: "CLOSE_IMPORT_COMPLETED",
};

// Create a context for the store
// Define the initial state
export const initialState: AppState = {
  // Website capture state
  url: "https://netflix.com/",
  urls: [],
  batchMode: false,
  viewports: ["1920"],
  theme: "light",

  // Import state
  uploadedFileName: null,
  parsedContent: null,

  // Shared state
  isLoading: false,
  error: null,
  isSuccess: false,
  availableFonts: [
    { value: "Inter", text: "Inter" },
    { value: "Roboto", text: "Roboto" },
    { value: "Helvetica", text: "Helvetica" },
    { value: "Arial", text: "Arial" },
    { value: "SF Pro", text: "SF Pro" },
  ],

  // User info
  figmaUID: "",
  figmaUserName: "",
  membership: "FREE",
  isBlocked: false,
  blockedUntil: null,

  // Storage
  savedWebsites: [],

  // Options
  options: {
    useAutoLayout: true,
    defaultFontFamily: "Inter",
    missingFontBehavior: "replace",
    extractImagesToSections: true,
    extractStyles: false,
    useExistingStyles: false,
    hoverComponents: false,
    addHyperlinks: true,
    htmlLayerNames: false,
  },

  // Upgrade modal state
  upgradeModal: {
    isOpen: false,
    reason: undefined,
    rateLimitEndTime: null,
  },

  // Post-import "Import completed" modal state
  importCompleted: {
    isOpen: false,
    sectionId: null,
    frameIds: [],
    fontReplacements: [],
  },
};

// Define the action types
type Action =
  | { type: typeof ActionTypes.SET_URL; payload: string }
  | { type: typeof ActionTypes.SET_URLS; payload: string[] }
  | { type: typeof ActionTypes.ADD_URL; payload: string }
  | { type: typeof ActionTypes.REMOVE_URL; payload: number }
  | { type: typeof ActionTypes.TOGGLE_BATCH_MODE }
  | { type: typeof ActionTypes.SET_VIEWPORTS; payload: string[] }
  | { type: typeof ActionTypes.SET_THEME; payload: string }
  | { type: typeof ActionTypes.SET_UPLOADED_FILE; payload: string | null }
  | { type: typeof ActionTypes.SET_PARSED_CONTENT; payload: any | null }
  | { type: typeof ActionTypes.RESET_IMPORT }
  | { type: typeof ActionTypes.SET_LOADING; payload: boolean }
  | { type: typeof ActionTypes.SET_ERROR; payload: string | null }
  | { type: typeof ActionTypes.SET_SUCCESS; payload: boolean }
  | {
      type: typeof ActionTypes.SET_AVAILABLE_FONTS;
      payload: Array<{ value: string; text: string }>;
    }
  | {
      type: typeof ActionTypes.SET_USER_INFO;
      payload: { id: string; name: string };
    }
  | { type: typeof ActionTypes.SET_MEMBERSHIP; payload: string }
  | {
      type: typeof ActionTypes.SET_USER_STATUS;
      payload: { rateLimit: { nextAvailableTime: string; cooldownRemaining?: number } | null };
    }
  | { type: typeof ActionTypes.SET_BLOCKED; payload: { isBlocked: boolean; blockedUntil?: number } }
  | { type: typeof ActionTypes.ADD_SAVED_WEBSITE; payload: string }
  | { type: typeof ActionTypes.SET_SAVED_WEBSITES; payload: string[] }
  | {
      type: typeof ActionTypes.UPDATE_OPTION;
      payload: { key: string; value: any };
    }
  | {
      type: typeof ActionTypes.OPEN_UPGRADE_MODAL;
      payload: { reason?: UpgradeReason; rateLimitEndTime?: string | null };
    }
  | { type: typeof ActionTypes.CLOSE_UPGRADE_MODAL }
  | {
      type: typeof ActionTypes.OPEN_IMPORT_COMPLETED;
      payload: {
        sectionId: string | null;
        fontReplacements: Array<{ original: string; matched: string; downloadUrl?: string }>;
      };
    }
  | { type: typeof ActionTypes.CLOSE_IMPORT_COMPLETED };

// Helper to extract payload from action (workaround for TypeScript discriminated union issue)
const getPayload = (action: Action): any => (action as any).payload;

// Create the reducer function
function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case ActionTypes.SET_URL:
      return { ...state, url: getPayload(action) };

    case ActionTypes.SET_URLS:
      return { ...state, urls: getPayload(action) };

    case ActionTypes.ADD_URL:
      if (getPayload(action) && !state.urls.includes(getPayload(action))) {
        return { ...state, urls: [...state.urls, getPayload(action)] };
      }
      return state;

    case ActionTypes.REMOVE_URL:
      return {
        ...state,
        urls: state.urls.filter((_, index) => index !== getPayload(action))
      };

    case ActionTypes.TOGGLE_BATCH_MODE:
      return { ...state, batchMode: !state.batchMode };

    case ActionTypes.SET_VIEWPORTS:
      return { ...state, viewports: getPayload(action) };

    case ActionTypes.SET_THEME:
      return { ...state, theme: getPayload(action) };

    case ActionTypes.SET_UPLOADED_FILE:
      return { ...state, uploadedFileName: getPayload(action) };

    case ActionTypes.SET_PARSED_CONTENT:
      return { ...state, parsedContent: getPayload(action) };

    case ActionTypes.RESET_IMPORT:
      return {
        ...state,
        uploadedFileName: null,
        parsedContent: null,
        error: null,
        isSuccess: false,
      };

    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: getPayload(action) };

    case ActionTypes.SET_ERROR:
      return { ...state, error: getPayload(action), isLoading: false };

    case ActionTypes.SET_SUCCESS:
      return { ...state, isSuccess: getPayload(action), isLoading: false };

    case ActionTypes.SET_AVAILABLE_FONTS:
      return { ...state, availableFonts: getPayload(action) };

    case ActionTypes.SET_USER_INFO:
      return {
        ...state,
        figmaUID: getPayload(action).id,
        figmaUserName: getPayload(action).name,
      };

    case ActionTypes.SET_MEMBERSHIP:
      return { ...state, membership: getPayload(action) };

    case ActionTypes.SET_USER_STATUS:
      return {
        ...state,
        userStatus: getPayload(action),
      };

    case ActionTypes.SET_BLOCKED:
      return {
        ...state,
        isBlocked: getPayload(action).isBlocked,
        blockedUntil: getPayload(action).blockedUntil || null,
      };

    case ActionTypes.ADD_SAVED_WEBSITE:
      // Newest first, deduped, capped. Shared with the persist sites so the stored copy and this
      // one cannot drift — see utils/saved-websites.
      return {
        ...state,
        savedWebsites: nextSavedWebsites(state.savedWebsites, getPayload(action)),
      };

    case ActionTypes.SET_SAVED_WEBSITES:
      return { ...state, savedWebsites: getPayload(action) };

    case ActionTypes.UPDATE_OPTION:
      return {
        ...state,
        options: {
          ...state.options,
          [getPayload(action).key]: getPayload(action).value,
        },
      };

    case ActionTypes.OPEN_UPGRADE_MODAL:
      /*
       * A PRO member is never shown this. Every reason it can carry renders the same
       * "Upgrade to Pro — $5.90" button, and `quota_exceeded` is titled "Free Limit
       * Reached" — selling a plan to someone who already bought it, and naming a limit
       * they do not have.
       *
       * REFUSED HERE rather than at the call sites, because the call sites are the bug.
       * The modal is opened from App, WebsiteCapture, HtmlCssCapture, ZipImport and
       * FileImport, and only ONE of those places checked membership first; the welcome
       * modal on first run checked nothing at all. A guard per dispatch is a guard someone
       * forgets, and four of them already had.
       *
       * It stays a dispatch rather than becoming a caller's responsibility so the blocked
       * and rate-limit states still update — those are recorded by their own actions, and
       * a Pro member who is blocked for suspicious activity is still blocked. What they
       * lose is the sales pitch, not the news.
       */
      if (state.membership === "PRO") return state;
      return {
        ...state,
        upgradeModal: {
          isOpen: true,
          reason: getPayload(action).reason || "general",
          rateLimitEndTime: getPayload(action).rateLimitEndTime || null,
        },
      };

    case ActionTypes.CLOSE_UPGRADE_MODAL:
      return {
        ...state,
        upgradeModal: {
          isOpen: false,
          reason: undefined,
          rateLimitEndTime: null,
        },
      };

    case ActionTypes.OPEN_IMPORT_COMPLETED:
      return {
        ...state,
        importCompleted: {
          isOpen: true,
          sectionId: getPayload(action).sectionId ?? null,
          frameIds: getPayload(action).frameIds ?? [],
          fontReplacements: getPayload(action).fontReplacements ?? [],
        },
      };

    case ActionTypes.CLOSE_IMPORT_COMPLETED:
      return {
        ...state,
        importCompleted: {
          ...state.importCompleted,
          isOpen: false,
        },
      };

    default:
      return state;
  }
}

/**
 * Zustand store holding the reducer state + a redux-style `dispatch`. Using
 * Zustand (rather than React Context) removes the provider from the render tree
 * and gives selector-based subscriptions for free, while keeping the existing
 * `reducer` + `actions` and the `{ state, dispatch }` consumer API unchanged.
 */
interface StoreShape {
  state: AppState;
  // Loosely typed (like the previous Context value) so components can hand
  // `dispatch` to helpers that accept plain action objects without a cast.
  dispatch: (action: any) => void;
}

export const useAppStore = create<StoreShape>((set, get) => ({
  state: initialState,
  dispatch: (action: Action) => set({ state: reducer(get().state, action) }),
}));

/**
 * Provider is a no-op with Zustand (the store is module-global) — kept so the
 * app tree and any `<StoreProvider>` usage stays the same.
 */
export function StoreProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/** `const { state, dispatch } = useStore()` — unchanged consumer API. */
export function useStore(): { state: AppState; dispatch: (action: any) => void } {
  const state = useAppStore((s) => s.state);
  const dispatch = useAppStore((s) => s.dispatch);
  return { state, dispatch };
}

// Action creators
export const actions = {
  setUrl: (url: string) => ({
    type: ActionTypes.SET_URL,
    payload: url,
  }),

  setUrls: (urls: string[]) => ({
    type: ActionTypes.SET_URLS,
    payload: urls,
  }),

  addUrl: (url: string) => ({
    type: ActionTypes.ADD_URL,
    payload: url,
  }),

  removeUrl: (index: number) => ({
    type: ActionTypes.REMOVE_URL,
    payload: index,
  }),

  toggleBatchMode: () => ({
    type: ActionTypes.TOGGLE_BATCH_MODE,
  }),

  setViewports: (viewports: string[]) => ({
    type: ActionTypes.SET_VIEWPORTS,
    payload: viewports,
  }),

  setTheme: (theme: string) => ({
    type: ActionTypes.SET_THEME,
    payload: theme,
  }),

  setUploadedFile: (fileName: string | null) => ({
    type: ActionTypes.SET_UPLOADED_FILE,
    payload: fileName,
  }),

  setParsedContent: (content: any | null) => ({
    type: ActionTypes.SET_PARSED_CONTENT,
    payload: content,
  }),

  resetImport: () => ({
    type: ActionTypes.RESET_IMPORT,
  }),

  setLoading: (isLoading: boolean) => ({
    type: ActionTypes.SET_LOADING,
    payload: isLoading,
  }),

  setError: (error: string | null) => ({
    type: ActionTypes.SET_ERROR,
    payload: error,
  }),

  setSuccess: (isSuccess: boolean) => ({
    type: ActionTypes.SET_SUCCESS,
    payload: isSuccess,
  }),

  setAvailableFonts: (fonts: Array<{ value: string; text: string }>) => ({
    type: ActionTypes.SET_AVAILABLE_FONTS,
    payload: fonts,
  }),

  setUserInfo: (id: string, name: string) => ({
    type: ActionTypes.SET_USER_INFO,
    payload: { id, name },
  }),

  setMembership: (membership: string) => ({
    type: ActionTypes.SET_MEMBERSHIP,
    payload: membership,
  }),

  setUserStatus: (userStatus: { rateLimit: { nextAvailableTime: string; cooldownRemaining?: number } | null }) => ({
    type: ActionTypes.SET_USER_STATUS,
    payload: userStatus,
  }),

  setBlocked: (isBlocked: boolean, blockedUntil?: number) => ({
    type: ActionTypes.SET_BLOCKED,
    payload: { isBlocked, blockedUntil },
  }),

  addSavedWebsite: (url: string) => ({
    type: ActionTypes.ADD_SAVED_WEBSITE,
    payload: url,
  }),

  setSavedWebsites: (websites: string[]) => ({
    type: ActionTypes.SET_SAVED_WEBSITES,
    payload: websites,
  }),

  updateOption: (key: string, value: any) => ({
    type: ActionTypes.UPDATE_OPTION,
    payload: { key, value },
  }),

  openUpgradeModal: (reason?: UpgradeReason, rateLimitEndTime?: string | null) => ({
    type: ActionTypes.OPEN_UPGRADE_MODAL,
    payload: { reason, rateLimitEndTime },
  }),

  closeUpgradeModal: () => ({
    type: ActionTypes.CLOSE_UPGRADE_MODAL,
  }),

  openImportCompleted: (payload: {
    sectionId: string | null;
    frameIds: string[];
    fontReplacements: Array<{ original: string; matched: string; downloadUrl?: string }>;
  }) => ({
    type: ActionTypes.OPEN_IMPORT_COMPLETED,
    payload,
  }),

  closeImportCompleted: () => ({
    type: ActionTypes.CLOSE_IMPORT_COMPLETED,
  }),
};
